import { inject, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { MatSnackBar } from '@angular/material/snack-bar'

import { throwError } from 'rxjs'
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators'

import { environment } from '@env'
import { AuthServices } from '@services/auth.services'
import { OperatorDataState } from '@states/operator-data'
import { setLocalStorage, STORAGE_KEYS } from '@utils/storage'

const TOKEN_URL = `${environment.endpoint}/security/oauth/token`
const SIGN_IN_ROUTE = '/auth/sign-in'
const SESSION_ENDED = 'Session ended'

let isRefreshing = false
const refreshedToken = signal<string | null>(null)

const withBearer = (req: HttpRequest<unknown>, token: string) => {
	return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) })
}

export const oauthInterceptor: HttpInterceptorFn = (req, next) => {
	const dialog = inject(MatDialog)
	const router = inject(Router)
	const snackBar = inject(MatSnackBar)
	const authService = inject(AuthServices)
	const operatorDataState = inject(OperatorDataState)
	const operatorData = operatorDataState.operatorData()
	const refreshedToken$ = toObservable(refreshedToken)

	if (req.url === TOKEN_URL || !operatorData) return next(req)

	const { access_token, refresh_token, requestPoint } = operatorData

	return next(withBearer(req, access_token)).pipe(
		catchError((err: HttpErrorResponse) => {
			if (err.status !== 401) return throwError(() => err)

			if (isRefreshing) {
				return refreshedToken$.pipe(
					filter((token): token is string => token !== null),
					take(1),
					switchMap(token => next(withBearer(req, token)))
				)
			}

			isRefreshing = true
			refreshedToken.set(null)

			return authService.refreshToken(refresh_token, requestPoint).pipe(
				switchMap(result => {
					setLocalStorage(STORAGE_KEYS.AUTH, result)

					refreshedToken.set(result.access_token)
					operatorDataState.operatorData.set(result)

					return next(withBearer(req, result.access_token))
				}),
				catchError((refreshErr: HttpErrorResponse) => {
					if (refreshErr.status === 401) {
						globalThis.localStorage.clear()

						operatorDataState.operatorData.set(null)
						dialog.closeAll()
						snackBar.dismiss()
						router.navigateByUrl(SIGN_IN_ROUTE)

						return throwError(() => SESSION_ENDED)
					}

					return throwError(() => refreshErr)
				}),
				finalize(() => {
					isRefreshing = false
				})
			)
		})
	)
}
